import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calculator, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NonDivValuationMatrixCard — Round 97a (Feb 2026).
 *
 * Replaces the DDM-based Valuation Matrix on the Insights tab when
 * the ticker is non-dividend (pays_dividend === false). Same physical
 * layout as ValuationMatrixCard so the Insights tab feels consistent;
 * different math because non-div stocks have no forward dividend to
 * discount.
 *
 * Three engines, server-routed via /api/stocks/{ticker}/non-div-valuation:
 *   1. Two-Stage FCFE Discount Model — used when TTM FCFE > 0 and
 *      projected growth < user's required return. Slider = required %.
 *   2. Owner Earnings Multiplier (OCF) — 3-yr avg OCF/share × multiple.
 *      Default when FCFE fails. Slider = multiple (8x–22x, anchor 15x).
 *   3. Owner Earnings Multiplier (EBITDA) — 3-yr avg EBITDA × multiple
 *      → EV → minus net debt → per share. Slider = multiple (6x–18x,
 *      anchor 12x).
 *
 * The OCF/EBITDA toggle pill is shown only when the fallback model is
 * active. When FCFE is the chosen model, the toggle is hidden so the
 * card matches the dividend card's single-slider feel.
 */

// Slider anchors per Gemini's research on industry-standard ranges.
const SLIDER_CONFIG = {
  fcfe:               { min: 6,  max: 15, mid: 10, step: 0.5, unit: '%',  label: 'required rate of return', abbrTerm: 'REQUIRED_RETURN' },
  ocf_multiplier:    { min: 8,  max: 22, mid: 15, step: 0.5, unit: 'x',  label: 'target P/OCF multiple', abbrTerm: 'OCF_MULTIPLIER' },
  ebitda_multiplier: { min: 6,  max: 18, mid: 12, step: 0.5, unit: 'x',  label: 'target EV/EBITDA multiple', abbrTerm: 'EV_EBITDA' },
};

function computeFcfeTwoStage({ fcfePerShare, growth, r }) {
  // Clamp growth so the math never blows up.
  const g1 = Math.min(Math.max(growth || 0, -0.1), 0.20);
  const g2 = 0.035; // long-run inflation + GDP floor (matches DDM card)
  const rr = Math.max(r, g1 + 0.01);
  // Stage 1: discount 5 years of growing FCFE
  let pv = 0;
  let cf = fcfePerShare;
  for (let t = 1; t <= 5; t++) {
    cf = cf * (1 + g1);
    pv += cf / Math.pow(1 + rr, t);
  }
  // Stage 2: terminal value, discounted back
  const terminal = (cf * (1 + g2)) / (rr - g2);
  pv += terminal / Math.pow(1 + rr, 5);
  return pv;
}

function computeOcfMultiplier({ avgOcfPerShare, multiple }) {
  if (!avgOcfPerShare || avgOcfPerShare <= 0) return null;
  return avgOcfPerShare * multiple;
}

function computeEbitdaMultiplier({ avgEbitda, multiple, totalDebt, totalCash, shares }) {
  if (!avgEbitda || avgEbitda <= 0 || !shares || shares <= 0) return null;
  const ev = avgEbitda * multiple;
  const equity = ev - (totalDebt || 0) + (totalCash || 0);
  if (equity <= 0) return 0;
  return equity / shares;
}

const fmtMoney = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;
const fmtBn = (v) => {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  return `$${v.toFixed(0)}`;
};

export default function NonDivValuationMatrixCard({ stock, livePrice }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // The user-selected model — defaults to whatever the backend
  // recommends but the user can flip between OCF and EBITDA via the
  // toggle pill once the multiplier fallback is active.
  const [model, setModel] = useState(null);
  const [sliderValue, setSliderValue] = useState(null);

  useEffect(() => {
    if (!stock?.ticker) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/stocks/${stock.ticker}/non-div-valuation`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setData(r.data || null);
        const rec = r.data?.recommended_model || 'unavailable';
        setModel(rec);
        // Seed slider at the model's midpoint anchor.
        const cfg = SLIDER_CONFIG[rec] || SLIDER_CONFIG.fcfe;
        setSliderValue(cfg.mid);
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setModel('unavailable');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [stock?.ticker]);

  // When the user flips the OCF/EBITDA toggle, snap the slider to
  // the new model's midpoint instead of leaving it at a value that
  // means something completely different on the other scale.
  useEffect(() => {
    if (!model) return;
    const cfg = SLIDER_CONFIG[model];
    if (cfg) setSliderValue(cfg.mid);
  }, [model]);

  const livePriceNum = Number(livePrice || stock?.price || 0);
  const cfg = SLIDER_CONFIG[model] || SLIDER_CONFIG.fcfe;

  const targetPrice = useMemo(() => {
    if (!data || sliderValue == null || model === 'unavailable') return null;
    if (model === 'fcfe') {
      return computeFcfeTwoStage({
        fcfePerShare: data.fcfe_per_share_ttm,
        growth: data.projected_growth_5y || 0,
        r: sliderValue / 100,
      });
    }
    if (model === 'ocf_multiplier') {
      return computeOcfMultiplier({
        avgOcfPerShare: data.avg_ocf_per_share_3y,
        multiple: sliderValue,
      });
    }
    if (model === 'ebitda_multiplier') {
      return computeEbitdaMultiplier({
        avgEbitda: data.avg_ebitda_3y,
        multiple: sliderValue,
        totalDebt: data.total_debt,
        totalCash: data.total_cash,
        shares: data.shares_outstanding,
      });
    }
    return null;
  }, [data, sliderValue, model]);

  const delta = targetPrice && livePriceNum > 0 ? (targetPrice - livePriceNum) / livePriceNum : null;
  const underValued = delta != null && delta > 0.05;
  const overValued = delta != null && delta < -0.05;

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-3" data-testid="non-div-valuation-loading">
        <div className="text-[11px] text-muted-foreground">Loading valuation data…</div>
      </div>
    );
  }
  if (!data || model === 'unavailable') {
    // Card hides itself if there's nothing to show — keeps the
    // Insights tab clean for tickers without enough financial data.
    return null;
  }

  // Round 96k (Feb 2026) — render OCF / EBITDA inside the badge as
  // AbbrTip-wrapped JSX (was a plain string) so the user can click the
  // acronym to read the definition. The unwrapped prefix ("Owner
  // Earnings · ") stays as plain text.
  const modelBadge = (() => {
    if (model === 'fcfe') return <AbbrTip term="TWO_STAGE_FCFE">Two-Stage FCFE</AbbrTip>;
    if (model === 'ocf_multiplier') return <>Owner Earnings · <AbbrTip term="OCF">OCF</AbbrTip></>;
    if (model === 'ebitda_multiplier') return <>Owner Earnings · <AbbrTip term="EBITDA">EBITDA</AbbrTip></>;
    return '—';
  })();

  const showToggle = model === 'ocf_multiplier' || model === 'ebitda_multiplier';

  return (
    <div className="rounded-lg border bg-card p-3" data-testid="non-div-valuation-card">
      {/* Header — mirrors ValuationMatrixCard's fuchsia heading. */}
      {/* Header */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <Calculator className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" />
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
            Valuation Matrix — Target Buy Price
          </h4>
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
          data-testid="non-div-valuation-model-badge"
          title={
            model === 'fcfe'
              ? 'Two-Stage FCFE valuation — projects 5y of FCFE growth then a terminal value.'
              : model === 'ocf_multiplier'
                ? 'Owner-Earnings / OCF multiplier fallback — used when FCFE math breaks.'
                : 'EV/EBITDA multiplier fallback — used when FCFE math breaks.'
          }
        >
          {modelBadge}
        </span>
      </div>

      {/* OCF/EBITDA toggle pill — only when fallback model is active. */}
      {showToggle && (
        <div className="flex items-center gap-1 mb-2" data-testid="non-div-valuation-toggle">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide mr-1">model:</span>
          <button
            type="button"
            onClick={() => setModel('ocf_multiplier')}
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${
              model === 'ocf_multiplier'
                ? 'bg-fuchsia-500 text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
            data-testid="non-div-valuation-tab-ocf"
          >
            OCF
          </button>
          <button
            type="button"
            onClick={() => setModel('ebitda_multiplier')}
            disabled={!data.avg_ebitda_3y || data.avg_ebitda_3y <= 0}
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              model === 'ebitda_multiplier'
                ? 'bg-fuchsia-500 text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
            data-testid="non-div-valuation-tab-ebitda"
          >
            EBITDA
          </button>
        </div>
      )}

      {/* Amber fallback banner — only when FCFE wasn't usable. */}
      {data.fallback_reason && model !== 'fcfe' && (
        <div
          className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2 mb-3"
          data-testid="non-div-valuation-fallback-banner"
        >
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>FCFE model unavailable — {data.fallback_reason} Showing relative valuation instead.</span>
        </div>
      )}

      {/* Slider */}
      <div className="mb-3 px-1">
        <div className="flex items-center justify-between text-[11px] font-bold text-foreground mb-1">
          <span>
            Your{' '}
            {cfg.abbrTerm
              ? <AbbrTip term={cfg.abbrTerm}>{cfg.label}</AbbrTip>
              : <span className="underline decoration-dotted">{cfg.label}</span>}
          </span>
          <span data-testid="non-div-valuation-slider-value">
            {sliderValue?.toFixed(1)}{cfg.unit}
          </span>
        </div>
        {/* Round 96j (Feb 2026) — Touch-shield wrapper, mirrors the
            dividend ValuationMatrixCard's shield. The StockModal's
            tab strip listens for left/right swipes on the outer
            container; without this shield, finger drags on the
            slider bubble up and trigger a tab change as soon as the
            user releases. We stop touch-event propagation here.
            6px (`py-1.5`) vertical padding gives a small buffer for
            slightly-imprecise finger taps above or below the rail. */}
        <div
          className="py-1.5"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          data-testid="non-div-valuation-slider-touch-shield"
        >
          <input
            type="range"
            min={cfg.min}
            max={cfg.max}
            step={cfg.step}
            value={sliderValue ?? cfg.mid}
            onChange={(e) => setSliderValue(Number(e.target.value))}
            className="w-full accent-fuchsia-500"
            data-testid="non-div-valuation-slider"
          />
        </div>
        <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
          <span>{cfg.min}{cfg.unit} conservative</span>
          <span>{cfg.mid}{cfg.unit} sector avg</span>
          <span>{cfg.max}{cfg.unit} aggressive</span>
        </div>
      </div>

      {/* 3-column readout — matches DDM card exactly. */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Current Price</div>
          <div className="text-base font-bold" data-testid="non-div-valuation-current-price">
            {fmtMoney(livePriceNum)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Target Buy</div>
          <div className="text-base font-bold text-fuchsia-500 dark:text-fuchsia-400" data-testid="non-div-valuation-target-price">
            {fmtMoney(targetPrice)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">VS Market</div>
          <div className="flex items-center gap-1" data-testid="non-div-valuation-vs-market">
            {delta != null ? (
              <>
                {underValued ? (
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                ) : overValued ? (
                  <TrendingDown className="w-3.5 h-3.5 text-rose-500" />
                ) : null}
                <span className={`text-base font-bold ${
                  underValued ? 'text-emerald-500' : overValued ? 'text-rose-500' : 'text-muted-foreground'
                }`}>
                  {delta > 0 ? '+' : ''}{(delta * 100).toFixed(1)}%
                </span>
                <span className="text-[10px] text-muted-foreground ml-0.5">
                  {underValued ? 'Undervalued' : overValued ? 'Overvalued' : 'Fair'}
                </span>
              </>
            ) : <span className="text-muted-foreground">—</span>}
          </div>
        </div>
      </div>

      {/* Assumptions strip — surfaces the underlying inputs so the
          user can see at a glance WHY the target landed where it did. */}
      <div className="mt-3 pt-2 border-t border-border/40 space-y-1 text-[11px] text-muted-foreground">
        {model === 'fcfe' && (
          <>
            <div>• TTM FCFE/share: <span className="text-foreground font-medium">{fmtMoney(data.fcfe_per_share_ttm)}</span></div>
            <div>• 5-yr projected growth (OCF CAGR): <span className="text-foreground font-medium">{data.projected_growth_5y != null ? `${(data.projected_growth_5y * 100).toFixed(2)}%` : '—'}</span></div>
            <div>• Required return (r): <span className="text-foreground font-medium">{sliderValue?.toFixed(1)}%</span></div>
          </>
        )}
        {model === 'ocf_multiplier' && (
          <>
            <div>• 3-yr avg OCF/share: <span className="text-foreground font-medium">{fmtMoney(data.avg_ocf_per_share_3y)}</span> <span className="text-[10px]">({data.ocf_years} yrs of data)</span></div>
            <div>• Slider multiple: <span className="text-foreground font-medium">{sliderValue?.toFixed(1)}x</span></div>
          </>
        )}
        {model === 'ebitda_multiplier' && (
          <>
            <div>• 3-yr avg EBITDA: <span className="text-foreground font-medium">{fmtBn(data.avg_ebitda_3y)}</span> <span className="text-[10px]">({data.ebitda_years} yrs of data)</span></div>
            <div>• Total debt − cash: <span className="text-foreground font-medium">{fmtBn((data.total_debt || 0) - (data.total_cash || 0))}</span></div>
            <div>• Shares outstanding: <span className="text-foreground font-medium">{fmtBn(data.shares_outstanding)}</span></div>
            <div>• Slider multiple: <span className="text-foreground font-medium">{sliderValue?.toFixed(1)}x</span></div>
          </>
        )}
      </div>

      {/* Explanatory footer — adapts to active model. */}
      <div className="mt-3 pt-2 border-t border-border/40 text-[11px] text-muted-foreground space-y-2">
        {model === 'fcfe' ? (
          <>
            <p>
              <span className="text-fuchsia-500 dark:text-fuchsia-400 font-semibold">What this model does</span> · The <AbbrTip term="TWO_STAGE_FCFE">Two-Stage FCFE</AbbrTip> model values the stock by discounting the cash it generates that&apos;s available to equity shareholders. Stage 1 projects 5 years of <AbbrTip term="FCFE">FCFE</AbbrTip> growth at the company&apos;s recent cash-flow <AbbrTip term="CAGR">CAGR</AbbrTip>; Stage 2 caps off with a terminal value at long-run inflation + GDP (~3.5%/yr).
            </p>
            <p>
              <span className="text-fuchsia-500 dark:text-fuchsia-400 font-semibold">Why the slider matters</span> · The <AbbrTip term="REQUIRED_RETURN">required rate of return</AbbrTip> is YOUR personal hurdle. Slide RIGHT (aggressive) and the model demands a cheaper entry. Slide LEFT (conservative) and the model is willing to pay more. If your r drops below the company&apos;s growth rate, we automatically swap to the <AbbrTip term="OWNER_EARNINGS_MULTIPLIER">Owner Earnings Multiplier</AbbrTip> model so the math stays grounded.
            </p>
          </>
        ) : (
          <>
            <p>
              <span className="text-fuchsia-500 dark:text-fuchsia-400 font-semibold">What this model does</span> · {model === 'ocf_multiplier'
                ? (<>The <AbbrTip term="OWNER_EARNINGS_MULTIPLIER">OCF Multiplier</AbbrTip> values the stock by multiplying its 3-year average Operating Cash Flow per share by your target P/OCF multiple. Smooths out single-year volatility and bypasses the balance sheet (cash flow already belongs to equity). DiviDrip swaps to this from the <AbbrTip term="TWO_STAGE_FCFE">Two-Stage FCFE</AbbrTip> model when projected <AbbrTip term="CAGR">CAGR</AbbrTip> exceeds your required return.</>)
                : (<>The <AbbrTip term="EV_EBITDA">EV/EBITDA</AbbrTip> Multiplier values the whole enterprise at your target EV/<AbbrTip term="EBITDA">EBITDA</AbbrTip> multiple, then strips out net debt to land at the equity value per share. Penalizes companies carrying heavy debt loads.</>)}
            </p>
            <p>
              <span className="text-fuchsia-500 dark:text-fuchsia-400 font-semibold">Why the slider matters</span> · The multiple sets your relative-value tolerance. Slide LEFT for a conservative entry (deeper margin of safety). Slide RIGHT to pay a premium typical for dominant industry leaders or high-growth tech.
            </p>
          </>
        )}
        {overValued && (
          <p>
            <span className="text-fuchsia-500 dark:text-fuchsia-400 font-semibold">Why &quot;Overvalued&quot;</span> · {model === 'fcfe'
              ? (<>Cash-flow models can&rsquo;t price in unannounced acquisitions, brand equity shifts, or sudden operating leverage. An overvalued tag means current cash flows alone don&rsquo;t justify the price — you&rsquo;re paying for the growth narrative.</>)
              : (<>When a company shows overvalued against historical multiples, the market is pricing in massive future operating scale that hasn&rsquo;t yet materialized in the financials. Pair with the <AbbrTip term="QUALITY_SCORE_NON_DIV">Quality Score</AbbrTip>, <AbbrTip term="ROIC">ROIC</AbbrTip>, and conviction in the story before acting.</>)}
          </p>
        )}
      </div>
    </div>
  );
}
